import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Avatar,
  Box,
  CircularProgress,
  Tooltip,
  MenuItem,
  Chip,
  Stack,
  Pagination,
  FormControl,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Info,
  ViewList,
  ViewModule,
  Description as DescriptionIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import Button from '../../components/UIKit/Button';
import TextField from '../../components/UIKit/TextField';
import Select from '../../components/UIKit/Select';
import Card from '../../components/UIKit/Card';
import SearchInput from '../../components/UIKit/SearchInput';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '../../components/UIKit/ToggleButtonGroup';
import { professionsAPI } from '../../services/api';
import { 
  fetchProfessions, 
  setPagination, 
  selectAllProfessions, 
  selectProfessionsLoading,
  selectProfessionsPagination
} from '../../redux/slices/professionsSlice';
import { fetchDepartments, selectAllDepartments } from '../../redux/slices/departmentsSlice';
import { fetchStatuses, selectAllStatuses } from '../../redux/slices/statusesSlice';
import { fetchLanguages, selectAllLanguages } from '../../redux/slices/languagesSlice';
import { useTranslation } from 'react-i18next';

const ProfessionManagement = () => {
  const { t } = useTranslation(['professions', 'common']);
  const dispatch = useDispatch();
  
  // Получаем данные из Redux
  const professions = useSelector(selectAllProfessions);
  const departments = useSelector(selectAllDepartments);
  const statuses = useSelector(selectAllStatuses);
  const languages = useSelector(selectAllLanguages);
  
  // Получаем состояние загрузки из Redux
  const professionsLoading = useSelector(selectProfessionsLoading);
  
  // Получаем данные пагинации из Redux
  const paginationFromRedux = useSelector(selectProfessionsPagination);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentProfession, setCurrentProfession] = useState({
    name: '',
    description: '',
    departmentId: '',
    statusId: '',
    languageId: '',
    mainTerm: null,
    terms: [],
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [selectedProfession, setSelectedProfession] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [viewMode, setViewMode] = useState(() => {
    // Default to card view on mobile, table on desktop
    return window.innerWidth < 768 ? 'card' : 'table';
  });
  const [userActionLoading, setUserActionLoading] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  
  // Filter state
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    department_id: '',
    language_id: '',
    status_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Add resize listener to update view mode
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768 && viewMode === 'table') {
        setViewMode('card');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [viewMode]);

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    // Загружаем данные из Redux
    const fetchParams = {
      page: paginationFromRedux.page, 
      limit: paginationFromRedux.limit,
      includeCreators: true
    };

    // Only add sort parameters if they are not empty
    if (sortBy && sortOrder) {
      fetchParams.sortBy = sortBy;
      fetchParams.sortOrder = sortOrder;
    }

    // Add search parameter if not empty
    if (search.trim()) {
      fetchParams.search = search.trim();
    }

    // Add filters if any are not empty
    const activeFilters = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        activeFilters[key] = value;
      }
    });

    if (Object.keys(activeFilters).length > 0) {
      fetchParams.filters = activeFilters;
    }

    dispatch(fetchProfessions(fetchParams));
    dispatch(fetchDepartments({ all: true, isCommon: 1 }));
    dispatch(fetchStatuses({ all: true, isCommon: 1 }));
    dispatch(fetchLanguages({ all: true, isCommon: 1 }));
  }, [paginationFromRedux.page, paginationFromRedux.limit, sortBy, sortOrder, search, filters, dispatch]);

  // Открыть диалог для создания новой профессии
  const handleOpenCreateDialog = () => {
    setCurrentProfession({
      name: '',
      description: '',
      departmentId: '',
      statusId: '',
      languageId: '',
      mainTerm: null,
      terms: [],
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  // Открыть диалог для редактирования профессии
  const handleOpenEditDialog = (profession) => {
    const departmentId = profession.department?.id || null;
    const statusId = profession.term_group?.MainTerm?.status?.id || null;
    const languageId = profession.term_group?.MainTerm?.language?.id || null;

    console.log('Редактируем профессию:', profession);

    setCurrentProfession({
      id: profession.id,
      name: profession.term_group?.MainTerm?.value || profession.term_group?.name || '',
      description: profession.term_group?.description || '',
      departmentId,
      statusId,
      languageId,
      mainTerm: profession.term_group?.MainTerm || null,
      terms: profession.term_group?.Terms || [],
    });

    console.log('Редактирование профессии:', currentProfession);

    setEditMode(true);
    setOpenDialog(true);
  };

  // Закрыть диалог
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Изменение значений полей формы
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentProfession((prev) => ({ ...prev, [name]: value }));
  };

  // Сохранение профессии (создание или обновление)
  const handleSaveProfession = async () => {
    try {
      if (!currentProfession.name.trim()) {
        setSnackbar({
          open: true,
          message: t(
            'professions:nameRequired'
          ),
          severity: 'error',
        });
        return;
      }

      if (!currentProfession.languageId) {
        setSnackbar({
          open: true,
          message: t(
            'professions:translationRequired'
          ),
          severity: 'error',
        });
        return;
      }

      setUserActionLoading(true);

      // Формируем данные для отправки на сервер в нужном формате
      const professionData = {
        mainTerm: {
          value: currentProfession.name,
          description: currentProfession.description,
          language_id: currentProfession.languageId, // Default to English
          term_type_id: 1, // Main term type
          status_id: currentProfession.statusId || null,
        },
        department_id: currentProfession.departmentId || null,
        status_id: currentProfession.statusId || null,
        terms: currentProfession.terms || [],
      };

      if (editMode && currentProfession.id) {
        // Добавляем id при обновлении профессии
        professionData.id = currentProfession.id;
      }

      console.log('Отправляем данные профессии:', professionData);

      if (editMode) {
        // Обновление существующей профессии
        console.log(
          `Обновляем профессию ID=${currentProfession.id} с данными:`,
          professionData
        );
        await professionsAPI.update(currentProfession.id, professionData);
        console.log('Профессия успешно обновлена');
        setSnackbar({
          open: true,
          message: t(
            'professions:updateSuccess'
          ),
          severity: 'success',
        });
      } else {
        // Создание новой профессии
        await professionsAPI.create(professionData);
        setSnackbar({
          open: true,
          message: t('professions:createSuccess'),
          severity: 'success',
        });
      }

      setOpenDialog(false);
      
      // Обновляем основной список профессий с пагинацией
      dispatch(fetchProfessions({ 
        page: paginationFromRedux.page, 
        limit: paginationFromRedux.limit,
        includeCreators: true 
      }));
    } catch (err) {
      console.error('Error saving profession:', err);
      setSnackbar({
        open: true,
        message: editMode
          ? t('professions:updateError')
          : t('professions:createError'),
        severity: 'error',
      });
    } finally {
      setUserActionLoading(false);
    }
  };

  // Открыть диалог подтверждения удаления
  const handleOpenDeleteConfirm = (id) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  // Закрыть диалог подтверждения удаления
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteId(null);
  };

  // Открыть модальное окно с описанием
  const handleOpenDescriptionModal = (profession) => {
    setSelectedProfession(profession);
    setDescriptionModalOpen(true);
  };

  // Закрыть модальное окно с описанием
  const handleCloseDescriptionModal = () => {
    setDescriptionModalOpen(false);
    setSelectedProfession(null);
  };

  // Удаление профессии
  const handleDeleteProfession = async () => {
    try {
      setUserActionLoading(true);
      await professionsAPI.delete(deleteId);

      setSnackbar({
        open: true,
        message: t('professions:deleteSuccess'),
        severity: 'success',
      });

      handleCloseDeleteConfirm();
      
      // Обновляем основной список профессий с пагинацией
      dispatch(fetchProfessions({ 
        page: paginationFromRedux.page, 
        limit: paginationFromRedux.limit,
        includeCreators: true 
      }));
    } catch (err) {
      console.error('Error deleting profession:', err);
      setSnackbar({
        open: true,
        message: t('professions:deleteError'),
        severity: 'error',
      });
    } finally {
      setUserActionLoading(false);
    }
  };

  // Закрыть снэкбар
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Add view mode handler
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  // Sorting handler
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Filter handlers
  const handleSearchChange = (value) => {
    setSearch(value);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setSearch('');
    setFilters({
      department_id: '',
      language_id: '',
      status_id: ''
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Check if there are any active filters
  const hasActiveFilters = () => {
    return search.trim() !== '' || 
           filters.department_id !== '' || 
           filters.language_id !== '' || 
           filters.status_id !== '';
  };

  // Обработчик изменения страницы пагинации
  const handlePageChange = (event, value) => {
    dispatch(setPagination({ page: value }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    dispatch(setPagination({ limit: newLimit, page: 1 }));
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          mb: 3,
          gap: 2,
        }}
      >
        <Typography variant="h5" component="h1">
          {t('professions:management')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
          }}
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            {t('professions:create')}
          </Button>
          <Tooltip title={t('common:actions.filter')}>
            <Button
              onClick={toggleFilters}
            >
              <FilterListIcon />
            </Button>
          </Tooltip>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
          >
            <ToggleButton value="table" aria-label={t('common:view.table')}>
              <Tooltip title={t('common:view.table')}>
                <ViewList />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="card" aria-label={t('common:view.card')}>
              <Tooltip title={t('common:view.card')}>
                <ViewModule />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Filters Section - Available for all view modes */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: '16px' }} elevation={0}>
          <Grid container spacing={2} alignItems="flex-end">
            {/* Search */}
            <Grid item xs={12} md={4}>
              <SearchInput
                value={search}
                onChange={handleSearchChange}
                placeholder={t('common:search')}
              />
            </Grid>
            
            {/* Department Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.department_id}
                  onChange={(e) => handleFilterChange('department_id', e.target.value)}
                  displayEmpty
                  label={t('professions:department')}
                >
                  <MenuItem value="">
                    <em>{t('common:all')}</em>
                  </MenuItem>
                  {Array.isArray(departments) && departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.term_group?.MainTerm?.value || dept.term_group?.name || dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Language Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.language_id}
                  onChange={(e) => handleFilterChange('language_id', e.target.value)}
                  displayEmpty
                  label={t('professions:translation')}
                >
                  <MenuItem value="">
                    <em>{t('common:all')}</em>
                  </MenuItem>
                  {Array.isArray(languages) && languages.map((lang) => (
                    <MenuItem key={lang.id} value={lang.id}>
                      {lang.term_group?.MainTerm?.value || lang.term_group?.name || lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <Select
                  value={filters.status_id}
                  onChange={(e) => handleFilterChange('status_id', e.target.value)}
                  displayEmpty
                  label={t('professions:status')}
                >
                  <MenuItem value="">
                    <em>{t('common:all')}</em>
                  </MenuItem>
                  {Array.isArray(statuses) && statuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                  size="large"
                  disabled={!hasActiveFilters()}
                >
                  {t('common:actions.clear')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {professionsLoading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          py: 8 
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            {t('common:loading')}
          </Typography>
        </Box>
      ) : viewMode === 'table' ? (
        <>
          <TableContainer elevation={0} component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('professions:name')} {getSortIcon('name')}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSort('department')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('professions:department')} {getSortIcon('department')}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSort('language')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('professions:translation')} {getSortIcon('language')}
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSort('status')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('professions:status')} {getSortIcon('status')}
                    </Box>
                  </TableCell>
                  <TableCell>{t('professions:description')}</TableCell>
                  <TableCell align="right">
                    {t('common:actions.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {professions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      {t('professions:noProfessions')}
                    </TableCell>
                  </TableRow>
                ) : (
                  professions.map((profession) => {
                    // Extract data from the new structure
                    const name = profession.term_group?.MainTerm?.value || profession.term_group?.name || '';
                    const description = profession.term_group?.description || '';
                    const departmentName = profession.department?.term_group?.MainTerm?.value || 
                                          profession.department?.term_group?.name || '';
                    const statusName = profession.term_group?.MainTerm?.status?.name || '';
                    const statusColor = profession.term_group?.MainTerm?.status?.color || '';
                    const translationName = profession.term_group?.MainTerm?.language?.iso2 || '';

                    return (
                      <TableRow key={profession.id}>
                        <TableCell>{name}</TableCell>
                        <TableCell>
                          {departmentName || (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              fontStyle="italic"
                            >
                              {t('professions:noDepartment')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {translationName || (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              fontStyle="italic"
                            >
                              {t('professions:noTranslation')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {statusName ? (
                            <Chip
                              label={statusName}
                              sx={{
                                backgroundColor: statusColor || undefined,
                                color: statusColor ? '#fff' : undefined,
                              }}
                            />
                          ) : (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              fontStyle="italic"
                            >
                              {t('professions:noStatus')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => description && handleOpenDescriptionModal(profession)}
                            disabled={!description}
                            sx={{ 
                              color: description ? 'primary.main' : 'text.disabled',
                              '&:hover': {
                                backgroundColor: description ? 'action.hover' : 'transparent'
                              }
                            }}
                          >
                            <DescriptionIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Tooltip title={t('common:actions.edit')}>
                              <IconButton
                                color="primary"
                                onClick={() => handleOpenEditDialog(profession)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('common:actions.delete')}>
                              <IconButton
                                color="error"
                                onClick={() =>
                                  handleOpenDeleteConfirm(profession.id)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 4,
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {t('common:pagination.rows_per_page')}
              </Typography>
              <Select
                value={paginationFromRedux.limit}
                onChange={handleChangeRowsPerPage}
                size="small"
                sx={{ minWidth: 80 }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </Box>

            {paginationFromRedux.pages > 1 && (
              <Pagination
                count={paginationFromRedux.pages}
                page={paginationFromRedux.page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            )}

            <Typography variant="body2" color="text.secondary">
              {t('common:pagination.showing_items', {
                from: Math.min(
                  (paginationFromRedux.page - 1) * paginationFromRedux.limit + 1,
                  paginationFromRedux.total
                ),
                to: Math.min(
                  paginationFromRedux.page * paginationFromRedux.limit,
                  paginationFromRedux.total
                ),
                total: paginationFromRedux.total,
              })}
            </Typography>
          </Box>
        </>
      ) : viewMode === 'card' ? (
        <>
          <Grid container spacing={3}>
            {professions.length > 0 ? (
              professions.map((profession) => {
                // Извлекаем данные из новой структуры
                const name = profession.term_group?.MainTerm?.value || profession.term_group?.name || '';
                const description = profession.term_group?.description || '';
                const departmentName = profession.department?.term_group?.MainTerm?.value || 
                                      profession.department?.term_group?.name || '';
                const statusName = profession.term_group?.MainTerm?.status?.name || '';
                const statusColor = profession.term_group?.MainTerm?.status?.color || '';
                const translationName = profession.term_group?.MainTerm?.language?.iso2 || '';

                return (
                  <Grid item xs={12} sm={6} md={4} key={profession.id}>
                    <Card
                      sx={{
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ mb: 2, position: 'relative' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <Typography
                            variant="h6"
                            sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5, flex: 1 }}
                          >
                            {name}
                          </Typography>
                          <Tooltip title={t('professions:viewDescription')}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDescriptionModal(profession)}
                              sx={{ 
                                color: 'primary.main',
                                mt: -0.5,
                                ml: 1
                              }}
                              disabled={!description}
                            >
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>

                      </Box>

                      <Stack spacing={1} sx={{ flexGrow: 1 }}>
                        {departmentName && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {t('professions:department')}:
                            </Typography>
                            <Typography variant="body2">
                              {departmentName}
                            </Typography>
                          </Box>
                        )}

                        {statusName && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {t('professions:status')}:
                            </Typography>
                            <Chip
                              label={statusName}
                              size="small"
                              sx={{
                                height: 24,
                                backgroundColor: statusColor || undefined,
                                color: statusColor ? '#fff' : undefined,
                              }}
                            />
                          </Box>
                        )}

                        {translationName && (
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.875rem' }}
                          >
                            {t('professions:translation')}: {translationName}
                          </Typography>
                        )}

                      </Stack>

                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          pt: 2,
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Tooltip title={t('common:actions.edit')}>
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenEditDialog(profession)}
                            size="small"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common:actions.delete')}>
                          <IconButton
                            color="error"
                            onClick={() =>
                              handleOpenDeleteConfirm(profession.id)
                            }
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h5">
                    {t('professions:noProfessions')}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2 }}>
                    {t('common.noDescription')}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 4,
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                {t('common:pagination.rows_per_page')}
              </Typography>
              <Select
                value={paginationFromRedux.limit}
                onChange={handleChangeRowsPerPage}
                size="small"
                sx={{ minWidth: 80 }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </Box>

            {paginationFromRedux.pages > 1 && (
              <Pagination
                count={paginationFromRedux.pages}
                page={paginationFromRedux.page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            )}

            <Typography variant="body2" color="text.secondary">
              {t('common:pagination.showing_items', {
                from: Math.min(
                  (paginationFromRedux.page - 1) * paginationFromRedux.limit + 1,
                  paginationFromRedux.total
                ),
                to: Math.min(
                  paginationFromRedux.page * paginationFromRedux.limit,
                  paginationFromRedux.total
                ),
                total: paginationFromRedux.total,
              })}
            </Typography>
          </Box>
        </>
      ) : (
        <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
          <Info color="info" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6">{t('professions:noProfessions')}</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {t('common.noDescription')}
          </Typography>
        </Paper>
      )}
      {/* Диалог создания/редактирования профессии */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          {editMode ? t('professions:edit') : t('professions:create')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                name="name"
                label={t('professions:name')}
                fullWidth
                value={currentProfession.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label={t('professions:description')}
                fullWidth
                multiline
                rows={4}
                value={currentProfession.description}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Select
                  name="departmentId"
                  value={currentProfession.departmentId}
                  onChange={handleInputChange}
                  label={t('professions:department')}
                >
                  <MenuItem value="">
                    <em>{t('common:none')}</em>
                  </MenuItem>
                  {Array.isArray(departments) ? departments.map((dept) => {
                    // Use the direct name property from the department data structure
                    const deptName = dept.name || `Department ${dept.id}`;
                    
                    return (
                      <MenuItem key={dept.id} value={dept.id}>
                        {deptName}
                      </MenuItem>
                    );
                  }) : (
                    <MenuItem disabled>
                      {t('common:loading')}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Select
                  name="statusId"
                  value={currentProfession.statusId}
                  onChange={handleInputChange}
                  label={t('professions:status')}
                >
                  <MenuItem value="">
                    <em>{t('common:none')}</em>
                  </MenuItem>
                  {Array.isArray(statuses) ? statuses.map((status) => {
                    const statusName = status.name || `Status ${status.id}`;
                    return (
                      <MenuItem key={status.id} value={status.id}>
                        {statusName}
                      </MenuItem>
                    );
                  }) : (
                    <MenuItem disabled>
                      {t('common:loading')}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Select
                  name="languageId"
                  value={currentProfession.languageId}
                  onChange={handleInputChange}
                  label={t('professions:translation')}
                  required
                >
                  <MenuItem value="">
                    <em>{t('common:none')}</em>
                  </MenuItem>
                  {Array.isArray(languages) ? languages.map((language) => {
                    const languageName = language.name || `Language ${language.id}`;
                    return (
                      <MenuItem key={language.id} value={language.id}>
                        {languageName}
                      </MenuItem>
                    );
                  }) : (
                    <MenuItem disabled>
                      {t('common:loading')}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="text"
            startIcon={<CloseIcon />}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSaveProfession}
            color="primary"
            variant="contained"
            startIcon={<CheckIcon />}
            disabled={userActionLoading || !currentProfession.name.trim() || !currentProfession.languageId}
          >
            {userActionLoading ? (
              <CircularProgress size={24} />
            ) : (
              t('common:actions.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            p: 2,
          },
        }}
      >
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Avatar
            sx={{
              bgcolor: 'error.main',
              width: 64,
              height: 64,
              margin: '0 auto',
              mb: 2,
            }}
          >
            <DeleteIcon sx={{ color: 'white', fontSize: 32 }} />
          </Avatar>
          <Typography
            variant="h5"
            sx={{ mb: 1, fontSize: '1.25rem', fontWeight: 600 }}
          >
            {t('common:confirmDelete')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('professions:deleteConfirmation')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              onClick={handleCloseDeleteConfirm}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              onClick={handleDeleteProfession}
              color="error"
              variant="contained"
              disabled={userActionLoading}
              startIcon={
                userActionLoading ? <CircularProgress size={20} /> : null
              }
            >
              {userActionLoading
                ? t('common:actions.deleting')
                : t('common:actions.delete')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Модальное окно с описанием профессии */}
      <Dialog
        open={descriptionModalOpen}
        onClose={handleCloseDescriptionModal}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            {selectedProfession?.term_group?.MainTerm?.value || selectedProfession?.term_group?.name || t('professions:description')}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              color: 'text.primary',
            }}
          >
            {selectedProfession?.term_group?.description || t('common:noDescription')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDescriptionModal}
            variant="outlined"
          >
            {t('common:actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Снэкбар для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProfessionManagement;
